<template>
  <div
    class="app-toast-container"
    :class="`app-toast-container--${position}`"
    aria-live="polite">
    <transition-group name="app-toast">
      <div
        v-for="toast in toasts"
        :key="toast.id"
        class="app-toast"
        :class="`app-toast--${toast.variant}`"
        role="alert">
        <div class="app-toast__content">
          <strong v-if="toast.title" class="app-toast__title">{{ toast.title }}</strong>
          <div class="app-toast__message" v-html="toast.message"></div>
        </div>
        <button
          type="button"
          class="app-toast__close"
          aria-label="Cerrar notificación"
          @click="remove(toast)">
          &times;
        </button>
        <span
          v-if="toast.duration > 0"
          class="app-toast__progress"
          :style="progressStyle(toast)"></span>
      </div>
    </transition-group>
  </div>
</template>

<script>
let toastId = 0

export default {
  name: 'AppToast',
  props: {
    position: {
      type: String,
      default: 'top-right',
      validator: value => ['top-right', 'top-left', 'bottom-right', 'bottom-left'].includes(value)
    },
    duration: {
      type: Number,
      default: 3000
    }
  },
  data () {
    return {
      toasts: []
    }
  },
  methods: {
    toast (message, options = {}) {
      return this.show(message, options)
    },
    show (message, options = {}) {
      const toast = {
        id: ++toastId,
        title: options.title || '',
        variant: options.variant || 'primary',
        duration: options.duration !== undefined ? options.duration : this.duration,
        message: message || ''
      }
      this.toasts.push(toast)
      if (toast.duration > 0) {
        toast.timer = setTimeout(() => this.remove(toast), toast.duration)
      }
      return toast
    },
    success (message, options = {}) {
      return this.show(message, { ...options, variant: 'success', title: options.title || 'Éxito' })
    },
    danger (message, options = {}) {
      return this.show(message, { ...options, variant: 'danger', title: options.title || 'Error' })
    },
    warning (message, options = {}) {
      return this.show(message, { ...options, variant: 'warning', title: options.title || 'Atención' })
    },
    info (message, options = {}) {
      return this.show(message, { ...options, variant: 'info', title: options.title || 'Información' })
    },
    remove (toast) {
      if (toast.timer) {
        clearTimeout(toast.timer)
        toast.timer = null
      }
      const index = this.toasts.indexOf(toast)
      if (index !== -1) {
        this.toasts.splice(index, 1)
      }
    },
    clear () {
      this.toasts.forEach(toast => {
        if (toast.timer) clearTimeout(toast.timer)
      })
      this.toasts = []
    },
    progressStyle (toast) {
      return { animationDuration: `${toast.duration}ms` }
    }
  },
  destroyed () {
    this.clear()
  }
}
</script>

<style scoped>
.app-toast-container {
  position: fixed;
  z-index: 1100;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: min(360px, calc(100vw - 2rem));
  padding: 0;
}

.app-toast-container--top-right {
  top: 1rem;
  right: 1rem;
}

.app-toast-container--top-left {
  top: 1rem;
  left: 1rem;
}

.app-toast-container--bottom-right {
  bottom: 1rem;
  right: 1rem;
}

.app-toast-container--bottom-left {
  bottom: 1rem;
  left: 1rem;
}

.app-toast {
  position: relative;
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  padding: 0.75rem 0.875rem;
  background: #fff;
  border: 1px solid var(--app-border-color, #e2e8f0);
  border-left: 4px solid var(--app-primary, #0f2835);
  border-radius: var(--app-border-radius-lg, 10px);
  box-shadow: var(--app-shadow-md, 0 4px 6px -1px rgba(0, 0, 0, 0.1));
  overflow: hidden;
}

.app-toast__content {
  flex: 1;
  min-width: 0;
}

.app-toast__title {
  display: block;
  margin-bottom: 0.125rem;
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--app-primary, #0f2835);
}

.app-toast__message {
  font-size: 0.8125rem;
  line-height: 1.4;
  color: var(--app-text-color, #374151);
}

.app-toast__close {
  padding: 0;
  border: none;
  background: transparent;
  font-size: 1.25rem;
  line-height: 0;
  color: #9ca3af;
  cursor: pointer;
  transition: color 0.15s ease;
}

.app-toast__close:focus {
  outline: none;
  box-shadow: none;
}

.app-toast__close:hover {
  color: #111827;
}

.app-toast__progress {
  position: absolute;
  left: 0;
  bottom: 0;
  height: 3px;
  background: var(--app-primary, #0f2835);
  animation: app-toast-progress linear forwards;
}

/* Variantes */
.app-toast--primary {
  border-left-color: var(--app-primary, #0f2835);
}

.app-toast--primary .app-toast__title {
  color: var(--app-primary, #0f2835);
}

.app-toast--primary .app-toast__progress {
  background: var(--app-primary, #0f2835);
}

.app-toast--secondary {
  border-left-color: var(--app-secondary, #13475c);
}

.app-toast--secondary .app-toast__title {
  color: var(--app-secondary, #13475c);
}

.app-toast--secondary .app-toast__progress {
  background: var(--app-secondary, #13475c);
}

.app-toast--success {
  border-left-color: var(--app-success, #34c38f);
}

.app-toast--success .app-toast__title {
  color: var(--app-success, #34c38f);
}

.app-toast--success .app-toast__progress {
  background: var(--app-success, #34c38f);
}

.app-toast--danger {
  border-left-color: var(--app-danger, #f46a6a);
}

.app-toast--danger .app-toast__title {
  color: var(--app-danger, #f46a6a);
}

.app-toast--danger .app-toast__progress {
  background: var(--app-danger, #f46a6a);
}

.app-toast--warning {
  border-left-color: var(--app-warning, #f1b44c);
}

.app-toast--warning .app-toast__title {
  color: var(--app-warning, #f1b44c);
}

.app-toast--warning .app-toast__progress {
  background: var(--app-warning, #f1b44c);
}

.app-toast--info {
  border-left-color: var(--app-info, #50a5f1);
}

.app-toast--info .app-toast__title {
  color: var(--app-info, #50a5f1);
}

.app-toast--info .app-toast__progress {
  background: var(--app-info, #50a5f1);
}

@keyframes app-toast-progress {
  from {
    width: 100%;
  }

  to {
    width: 0;
  }
}

/* Transiciones */
.app-toast-enter-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.app-toast-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.app-toast-enter,
.app-toast-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}
</style>
